import React, { useState } from "react";
import { connect } from "react-redux";
import { actionCancelSubscription } from "../store/actions/subscriptionAction";

const CancelSubscriptionModal = ({
    show,
    onHide,
    subscription,
    actionCancelSubscription,
    subscriptionCancelLoader
}) => {
    const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true);

    // Get subscription ID from multiple possible fields
    const subscriptionId = subscription?.id ||
        subscription?.subscription_id ||
        subscription?.stripe_subscription_id;

    const handleCancel = async () => {
        if (!subscriptionId) {
            alert("Error: Subscription ID not found. Please contact support.");
            return;
        }

        try {
            await actionCancelSubscription(subscriptionId, cancelAtPeriodEnd);
            onHide();
            // Optionally refresh user data
            window.location.reload();
        } catch (error) {
            // Error already handled in action
            console.error("Cancel subscription error:", error);
        }
    };

    if (!show) return null;

    return (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fas fa-times-circle text-danger me-2"></i>
                            Cancel Subscription
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onHide}
                            disabled={subscriptionCancelLoader}
                        />
                    </div>
                    <div className="modal-body">
                        {/* Debug info - remove in production */}
                        {process.env.NODE_ENV === 'development' && (
                            <div className="alert alert-info small mb-3">
                                <strong>Debug Info:</strong><br />
                                Subscription ID: {subscriptionId || 'undefined'}<br />
                                Available fields: {subscription ? Object.keys(subscription).join(', ') : 'none'}
                            </div>
                        )}

                        <div className="alert alert-warning d-flex align-items-start" role="alert">
                            <i className="fas fa-exclamation-triangle me-2 mt-1"></i>
                            <div>
                                <strong>Warning:</strong> You are about to cancel your subscription.
                                This action cannot be undone.
                            </div>
                        </div>

                        {subscription && (
                            <div className="mb-4">
                                <h6 className="fw-bold mb-3">Current Subscription Details:</h6>
                                <div className="row text-sm">
                                    <div className="col-sm-4 text-muted">Plan:</div>
                                    <div className="col-sm-8">{subscription.plan?.name || "Active Plan"}</div>
                                </div>
                                <div className="row text-sm">
                                    <div className="col-sm-4 text-muted">Status:</div>
                                    <div className="col-sm-8">
                                        <span className="text-success text-uppercase fw-semibold">
                                            {subscription.status}
                                        </span>
                                    </div>
                                </div>
                                {subscription.current_period_end && (
                                    <div className="row text-sm">
                                        <div className="col-sm-4 text-muted">Current period ends:</div>
                                        <div className="col-sm-8">
                                            {new Date(subscription.current_period_end).toLocaleDateString()}
                                        </div>
                                    </div>
                                )}
                                {subscriptionId && (
                                    <div className="row text-sm">
                                        <div className="col-sm-4 text-muted">Subscription ID:</div>
                                        <div className="col-sm-8 font-monospace small">{subscriptionId}</div>
                                    </div>
                                )}
                            </div>
                        )}

                        {!subscriptionId && (
                            <div className="alert alert-danger" role="alert">
                                <i className="fas fa-exclamation-circle me-2"></i>
                                <strong>Error:</strong> Unable to find subscription ID. Please contact support for assistance.
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="form-label fw-semibold">Cancellation Options:</label>
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="cancelOption"
                                    id="cancelAtPeriodEnd"
                                    checked={cancelAtPeriodEnd}
                                    onChange={() => setCancelAtPeriodEnd(true)}
                                />
                                <label className="form-check-label" htmlFor="cancelAtPeriodEnd">
                                    <strong>Cancel at period end</strong>
                                    <div className="small text-muted">
                                        Your subscription will remain active until the end of your current billing period.
                                        You'll continue to have access to all features until then.
                                    </div>
                                </label>
                            </div>
                            <div className="form-check mt-2">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="cancelOption"
                                    id="cancelImmediately"
                                    checked={!cancelAtPeriodEnd}
                                    onChange={() => setCancelAtPeriodEnd(false)}
                                />
                                <label className="form-check-label" htmlFor="cancelImmediately">
                                    <strong>Cancel immediately</strong>
                                    <div className="small text-muted">
                                        Your subscription will be canceled immediately and you'll lose access to all features right away.
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onHide}
                            disabled={subscriptionCancelLoader}
                        >
                            Keep Subscription
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={handleCancel}
                            disabled={subscriptionCancelLoader || !subscriptionId}
                        >
                            {subscriptionCancelLoader ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                    Canceling...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-times me-2"></i>
                                    Cancel Subscription
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    subscriptionCancelLoader: state.subscription?.subscriptionCancelLoader || false,
});

export default connect(mapStateToProps, { actionCancelSubscription })(CancelSubscriptionModal);
